import { environment } from '@back-office/core';
import { joiResolver } from '@hookform/resolvers/joi';
import CryptoJS from 'crypto-js';
import { useLocalStore } from 'mobx-react';
import {
  BaseSyntheticEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  Control,
  FieldValues,
  FormState,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  BUTTON_CLICK_WAIT,
  FORGOT_PASSWORD_CLICK_TIME,
  ResponseMessage,
} from '../../../constants';
import { LoginForm, loginSchema, LoginStep } from '../../../forms';
import useInject from '../../../hooks/useInject';

type UseViewModelReturnType = {
  control: Control<LoginForm>;
  formState: FormState<LoginForm>;
  handleOnSubmit: (data: LoginForm) => Promise<void>;
  handleSubmit: <TSubmitFieldValues extends FieldValues = LoginForm>(
    onValid: SubmitHandler<TSubmitFieldValues>,
    onInvalid?: SubmitErrorHandler<LoginForm> | undefined
  ) => (e?: BaseSyntheticEvent) => Promise<void>;
  forgotPasswordButtonWaitTime: number;
  setForgotPasswordButtonWaitTime: Dispatch<SetStateAction<number>>;
  state: {
    errorMsg: string;
  };
  setLoginStep: (step: LoginStep) => void;
};

const useViewModel = (): UseViewModelReturnType => {
  const [forgotPasswordButtonWaitTime, setForgotPasswordButtonWaitTime] =
    useState<number>(0);
  const { control, handleSubmit, formState } = useForm<LoginForm>({
    mode: 'onChange',
    resolver: joiResolver(loginSchema),
  });
  const { setLoginStep, requestLoginOTP } = useInject((root) => root);

  const state = useLocalStore(() => ({
    errorMsg: '',
  }));

  const handleOnSubmit = async (data: LoginForm): Promise<void> => {
    const encryptedPassword = CryptoJS.AES.encrypt(
      data.password,
      environment.aesKey
    ).toString();
    const responseMsg: string = await requestLoginOTP({
      email: data.email,
      password: encryptedPassword,
    });
    if (responseMsg === ResponseMessage.OK) {
      setLoginStep(LoginStep.EnterOTP);
    } else {
      state.errorMsg = responseMsg;
    }
  };

  const differenceInSeconds = (from: Date, to: Date): number =>
    Math.abs(to.valueOf() - from.valueOf()) / 1000;

  useEffect(() => {
    let intervalID: number;
    const forgotClickTime = window.sessionStorage.getItem(
      FORGOT_PASSWORD_CLICK_TIME
    );

    if (forgotClickTime != null) {
      const diff = differenceInSeconds(new Date(forgotClickTime), new Date());
      if (diff <= BUTTON_CLICK_WAIT) {
        setForgotPasswordButtonWaitTime(Math.round(BUTTON_CLICK_WAIT - diff));
        intervalID = window.setInterval(() => {
          setForgotPasswordButtonWaitTime((duration) => {
            if (duration <= 0) {
              clearInterval(intervalID);
            }
            return duration - 1;
          });
        }, 1000);
      } else {
        window.sessionStorage.removeItem(FORGOT_PASSWORD_CLICK_TIME);
        setForgotPasswordButtonWaitTime(0);
      }
    }
    return () => clearInterval(intervalID);
  }, []);

  return {
    control,
    formState,
    handleOnSubmit,
    handleSubmit,
    forgotPasswordButtonWaitTime,
    setForgotPasswordButtonWaitTime,
    state,
    setLoginStep,
  };
};

export default useViewModel;
